// 歌曲長度：時間軸的時間尺、可捲動範圍、播放、縮放、「整段看完」都照這個長度走。
// 來源優先順序：
//   1. 有影片 → 影片的實際長度（不能改，避免和檔案對不上）
//   2. 有音樂（沒影片）→ 音樂的實際長度（不能改）
//   3. 都沒有 → 這首歌自己設的長度（導演、專案製作、全站製作可以改；songs.length_sec）
//   4. 也沒設 → 維持原本的算法（最後一個機關走完再多 4 秒，至少 10 秒）
// cue 或機關動作超出長度 → 時間軸延伸到那裡，不藏起來，也不自動改 cue 的時間。
// 長度只影響時間軸和預覽，不做任何工程警告。

export type SongLengthSource = 'video' | 'music' | 'custom' | 'auto';

export interface SongLengthInfo {
  /** 要用的長度（秒）；auto = null（照原本的算法） */
  seconds: number | null;
  source: SongLengthSource;
  /** 能不能改：只有「都沒有影片和音樂」才能改 */
  editable: boolean;
}

const pos = (n: unknown): number | null => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null);

export function songLengthInfo(o: { videoSec?: number | null; musicSec?: number | null; customSec?: number | null; hasVideo?: boolean; hasMusic?: boolean }): SongLengthInfo {
  const v = pos(o.videoSec);
  if (v !== null) return { seconds: v, source: 'video', editable: false };
  const m = pos(o.musicSec);
  if (m !== null) return { seconds: m, source: 'music', editable: false };
  // 有影片 / 音樂但長度還沒量到（載入中）：先別讓人改，免得跟檔案對不上
  if (o.hasVideo) return { seconds: null, source: 'video', editable: false };
  if (o.hasMusic) return { seconds: null, source: 'music', editable: false };
  const c = pos(o.customSec);
  if (c !== null) return { seconds: c, source: 'custom', editable: true };
  return { seconds: null, source: 'auto', editable: true };
}

/** 時間尺要畫多長：照歌曲長度；動作 / cue 超出就延伸；沒有長度 → 原本的算法 */
export function totalLength(o: { endTime: number; single: boolean; lengthSec?: number | null; clockDuration?: number | null; lastCueTime?: number | null }): number {
  const len = pos(o.lengthSec) ?? pos(o.clockDuration);
  const reach = Math.max(o.endTime, pos(o.lastCueTime) ?? 0);
  if (len !== null) return Math.max(len, reach);
  return o.single ? Math.max(4, reach + 1.5) : Math.max(10, reach + 4);
}

/** 「分:秒」打字：3:45 或 225 或 1:02:03 → 秒；打不出來 → null。範圍 1 秒到 24 小時 */
export function parseLengthInput(text: string): number | null {
  const t = text.trim().replace(/秒$/u, '').trim();
  if (!t) return null;
  const parts = t.split(':');
  if (parts.length > 3 || parts.some(p => !/^\d+(\.\d+)?$/.test(p.trim()))) return null;
  const nums = parts.map(Number);
  let sec = 0;
  for (const n of nums) sec = sec * 60 + n;
  if (!Number.isFinite(sec) || sec < 1 || sec > 86400) return null;
  return Math.round(sec * 1000) / 1000;
}

/** 秒 → 3:45（超過一小時 1:02:03）；小數最多一位 */
export function formatLength(sec: number): string {
  const s = Math.max(0, Math.round(sec * 10) / 10);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s - h * 3600 - m * 60;
  const rs = Number.isInteger(r) ? String(r).padStart(2, '0') : r.toFixed(1).padStart(4, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${rs}` : `${m}:${rs}`;
}

export const LENGTH_TEXT = {
  label: '歌曲長度',
  placeholder: '例：3:45 或 225',
  bad: '請打像 3:45 或 225（秒）這樣，1 秒到 24 小時之間。',
  clear: '清掉（回到原本的算法）',
  source: (s: SongLengthSource) => s === 'video' ? '來自影片的長度，不能改' : s === 'music' ? '來自音樂的長度，不能改' : s === 'custom' ? '這首歌自己設的長度' : '還沒設，時間軸照最後一個動作估',
  hint: '只影響時間軸和預覽。cue 或動作超過長度時，時間軸會自己延伸到那裡，不會藏起來。',
  readonly: '只有導演和 PM 可以改歌曲長度。',
  done: (t: string) => `歌曲長度設成 ${t}`,
  cleared: '歌曲長度已清掉，回到原本的算法',
} as const;
